import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type KeyboardEvent, type ReactNode, type RefObject } from 'react'
import { cx } from './cx'
import { boundsFor } from '../../layout/segments'

type PopoverEl = HTMLDivElement & { showPopover?: () => void; hidePopover?: () => void }

export interface FloatingPanelProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'onKeyDown'> {
  /** Elemento a cui il pannello si aggancia (sotto, o sopra se manca spazio). */
  anchorRef: RefObject<HTMLElement | null>
  open: boolean
  /** Chiusura da click fuori, scorrimento o ridimensionamento. */
  onClose: () => void
  /** Larghezza uguale a quella dell'ancora. */
  matchWidth?: boolean | undefined
  /** Sotto i 600px il pannello e' centrato sullo schermo con scrim (modale). */
  modalOnCompact?: boolean | undefined
  onKeyDown?: ((e: KeyboardEvent<HTMLDivElement>) => void) | undefined
  children: ReactNode
}

const EDGE = 8
const GAP = 4

/** Pannello nel top layer (popover manuale), posizionato sotto l'ancora con flip e scorrimento interno. */
export function FloatingPanel({ anchorRef, open, onClose, matchWidth, modalOnCompact, className, style, children, ...rest }: FloatingPanelProps) {
  const ref = useRef<PopoverEl | null>(null)
  const [pos, setPos] = useState<{ left: number; top: number; width: number | undefined; maxHeight: number; origin: 'top' | 'bottom' } | null>(null)
  const [modal, setModal] = useState(false)

  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    const panel = ref.current
    const anchor = anchorRef.current
    if (!panel || !anchor) return
    try { panel.showPopover?.() } catch { /* senza supporto resta position: fixed */ }
    const isModal = Boolean(modalOnCompact) && window.innerWidth < 600
    setModal(isModal)
    if (isModal) {
      setPos(null)
      return
    }
    const a = anchor.getBoundingClientRect()
    panel.style.maxHeight = 'none'
    const m = panel.getBoundingClientRect()
    const bd = boundsFor(a)
    const below = bd.bottom - a.bottom - GAP - EDGE
    const above = a.top - bd.top - GAP - EDGE
    const flip = m.height > below && above > below
    const space = Math.max(120, flip ? above : below)
    const h = Math.min(m.height, space)
    const top = flip ? Math.max(bd.top + EDGE, a.top - GAP - h) : a.bottom + GAP
    const width = matchWidth ? a.width : undefined
    const w = width ?? m.width
    let left = a.left
    if (left + w > bd.right - EDGE) left = a.right - w
    left = Math.max(bd.left + EDGE, left)
    setPos({ left, top, width, maxHeight: space, origin: flip ? 'bottom' : 'top' })
  }, [open, anchorRef, matchWidth, modalOnCompact])

  useEffect(() => {
    if (!open) return undefined
    const w0 = window.innerWidth
    const onDown = (e: PointerEvent) => {
      const panel = ref.current
      if (!panel) return
      const t = e.target as Node
      if (anchorRef.current?.contains(t)) return
      const r = panel.getBoundingClientRect()
      const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom
      if (panel.contains(t) && inside) return
      onClose()
    }
    const onScroll = (e: Event) => {
      if (modal) return
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return
      onClose()
    }
    const onResize = () => {
      if (window.innerWidth !== w0) onClose()
    }
    document.addEventListener('pointerdown', onDown, true)
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open, onClose, anchorRef, modal])

  if (!open) return null
  const st: CSSProperties = pos
    ? { left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.maxHeight, ...style }
    : modal
      ? { ...style }
      : { left: 0, top: 0, visibility: 'hidden', ...style }
  return (
    <div
      {...rest}
      ref={ref}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...({ popover: 'manual' } as any)}
      className={cx('md-floating', modal && 'md-floating--modal', className)}
      data-origin={pos?.origin}
      style={st}
    >
      {children}
    </div>
  )
}
